import { useQueryClient } from '@tanstack/react-query';
import { trackEvent } from 'analytics';
import { EventType } from 'analytics/models';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useAddTurnaroundSetToDelivery } from 'api/hooks/useAddTurnaroundSetToDelivery';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import { useDelivery } from 'api/hooks/useDelivery';
import { useSaveAllSetsInspections } from 'api/hooks/useSaveAllSetsInspections';
import { QueryKeys } from 'api/models/QueryKeys';
import { TrayImagePathParams } from 'api/models/Trays';
import { useLanguage } from 'context/AppContext';
import { useCheckOutTraysBySet } from 'hooks/useCheckOutTraysBySet';
import useGoogleStorageHelpers from 'utils/helpers/googleStorage.helpers';

type Props = {
  deliveryId: string;
};

export const useSaveCheckOutTrayInspectionsForSet = ({ deliveryId }: Props) => {
  const lang = useLanguage()[0].CheckOutTrayDetailsPage;
  const [checkOutTraysBySet] = useCheckOutTraysBySet();

  const { data: delivery, refetch: fetchDelivery } = useDelivery(deliveryId);
  const { data: currentUser } = useCurrentUser();

  const [isLoading, setIsLoading] = useState(false);

  const { uploadImage, constructTrayImagePath } = useGoogleStorageHelpers();
  const { mutateAsync: saveAllSetsInspections } = useSaveAllSetsInspections();
  const { mutateAsync: addTurnaroundSetToDelivery } = useAddTurnaroundSetToDelivery();

  const queryClient = useQueryClient();

  const saveCheckOutTrayInspectionsForSet = useCallback(
    async (setKitInstanceId: string) => {
      if (!delivery) {
        throw new Error('Delivery not found');
      }

      if (!currentUser) {
        throw new Error('User not found');
      }

      setIsLoading(true);

      const checkOutSet = checkOutTraysBySet[setKitInstanceId];
      const trays = checkOutSet.trays;

      // upload all tray images and collect the gsImagePath
      const gsImagePaths: Record<string, string> = {};

      try {
        // wait for any uploads already queued in the background (from image capture) to settle;
        // allSettled so a straggler failure doesn't stop us checking the others / falling back
        const backgroundUploads: Array<Promise<unknown>> = [];
        for (const trayId in trays) {
          if (trays[trayId].uploadPromise) {
            backgroundUploads.push(trays[trayId].uploadPromise as Promise<unknown>);
          }
        }
        await Promise.allSettled(backgroundUploads);

        const imagePromises: Array<Promise<unknown>> = [];

        for (const trayId in trays) {
          const tray = trays[trayId];

          if (tray.gsImagePath) {
            // the background upload (or a previous save) already produced a path
            gsImagePaths[trayId] = tray.gsImagePath;
          } else if (tray.imageDataUri) {
            // no background upload succeeded (never queued, or it failed) - fall back to
            // uploading it now
            imagePromises.push(
              (async () => {
                const params: TrayImagePathParams = {
                  stage: 'CHECK_OUT',
                  deliveryId,
                  setId: setKitInstanceId,
                  trayId,
                };

                const constructedPath = constructTrayImagePath(params);

                // upload the image to firebase storage
                const gsImagePath = await uploadImage(constructedPath, tray.imageDataUri as string);

                if (!gsImagePath) {
                  throw new Error('Failed to upload tray image');
                }

                gsImagePaths[trayId] = gsImagePath;
              })(),
            );
          }

          trackEvent(EventType.CompleteTrayCheckOut, {
            status: delivery.status,
            order_id: delivery.orderNumber,
            received_by: delivery.receivedDetails?.receivedBy || '',
            set_id: setKitInstanceId,
            tray_instance_id: trayId,
            all_clean: tray.allDevicesCleanOfDebris,
            all_functional: tray.allDevicesFunctional,
            all_parts_present: tray.allDevicesInTray,
            tray_used: tray.trayUsed,
            current_user: currentUser.userId,
            tenant_name: currentUser.tenantName,
          });
        }

        // run the fallback image upload promises
        await Promise.all(imagePromises);

        const allTraysInspection = Object.entries(trays).map(([trayId, tray]) => ({
          trayInstanceId: tray.trayInstanceId || null,
          trayName: tray.name,
          // carry the existing tray reference through check out; it is set during check in and must not change here
          trayReference: tray.trayReference ?? null,
          gsImagePath: gsImagePaths[trayId] || '',
          decantedTrays: [],
          trayInspection: {
            allClean: tray.allDevicesCleanOfDebris ?? true,
            allFunctional: tray.allDevicesFunctional ?? true,
            allPartsPresent: tray.allDevicesInTray ?? true,
            trayUsed: !!tray.trayUsed,
            missingParts: tray.missingParts || [],
            missingPartsComment: tray.missingPartsComment || '',
            trayReference: tray.trayReference ?? null,
          },
          comments: tray.comments || '',
          stage: 'CHECK_OUT',
          status: 'CONFIRMED' as any,
        }));

        // save all inspection data
        await saveAllSetsInspections({
          deliveryId,
          payload: { [setKitInstanceId]: { allTraysInspection } },
        });

        // fire and forget logging
        trackEvent(EventType.CompleteSetCheckOut, {
          delivery_id: '' + delivery.id,
          order_id: delivery.orderNumber,
          received_by: delivery.receivedDetails?.receivedBy || '',
          set_id: setKitInstanceId,
          set_display_code: setKitInstanceId,
          set_kit_instance_id: setKitInstanceId,
          current_user: currentUser.userId,
          tenant_name: currentUser.tenantName,
        });

        toast.success(lang.setCheckOutSuccess);

        // if the set is being truned around save the assdociation
        if (checkOutSet.turnaroundDeliveryId) {
          await addTurnaroundSetToDelivery({
            deliveryId: checkOutSet.turnaroundDeliveryId,
            request: {
              kit_instance_id: setKitInstanceId,
            },
          });
        }

        const { data: updatedDelivery } = await fetchDelivery();

        // ensure states are updated
        queryClient.invalidateQueries([QueryKeys.Order, delivery.orderRecordId]);
        queryClient.invalidateQueries([QueryKeys.Orders]);
        queryClient.invalidateQueries([QueryKeys.TimeStamps]);
        queryClient.invalidateQueries([QueryKeys.SurgeryTimeStamps]);

        // log the complete delivery check out event
        if (updatedDelivery?.status === 'CHECKED_OUT') {
          // fire and forget
          trackEvent(EventType.CompleteDeliveryCheckOut, {
            delivery_id: deliveryId,
            order_id: delivery.orderNumber,
            received_by: delivery.receivedDetails?.receivedBy || '',
            no_road_cases: delivery.shipperDetails?.numRoadCases || null,
            no_boxes: delivery.shipperDetails?.numBoxes || null,
            num_tubs: delivery.shipperDetails?.numTubs || null,
            no_pelican_cases: delivery.shipperDetails?.numPelicanCases || null,
            no_double_road_cases: delivery.shipperDetails?.numDoubleRoadCases || null,
            current_user: currentUser.userId,
            tenant_name: currentUser.tenantName,
          }).catch(() => {});
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      addTurnaroundSetToDelivery,
      checkOutTraysBySet,
      constructTrayImagePath,
      currentUser,
      delivery,
      deliveryId,
      fetchDelivery,
      lang.setCheckOutSuccess,
      queryClient,
      saveAllSetsInspections,
      uploadImage,
    ],
  );

  return {
    mutateAsync: saveCheckOutTrayInspectionsForSet,
    isLoading,
  };
};
